import {
  PreviewHeading,
  PreviewParagraph,
  PreviewSegment,
  ProcessedClauseChange,
  UnitPreview,
} from "common/flowmaster/types/amendment";
import {ClauseAtom} from "common/types/document";
import {stripClauseNumberPrefix} from "word/taskpane/routes/checklist/utils/amendments/clause_number_utils";
import {applyEdit, isTouched, plainText} from "./segment_editor";
import {
  formatAtomLabel,
  getUnitDepth,
  isInUnit,
  normaliseReference,
} from "./unit_key";

const COLLAPSE_ABOVE_CHARS = 1500;
const MAX_GAP_AFTER_BEFORE = 80;
const MIN_CONTEXT_WORDS = 3;
const MAX_CONTEXT_WORDS = 8;

// Length-preserving so positions stay aligned
const normalizeQuotes = (text: string): string =>
  text
    .replace(/[‘’`´]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/\u00a0/g, " ");

const clean = (text: string | undefined): string =>
  normalizeQuotes((text ?? "").trim());

interface Span {
  start: number;
  end: number;
}

function findExact(text: string, needle: string, from: number): Span | null {
  if (!needle) {
    return null;
  }
  const idx = text.indexOf(needle, from);
  if (idx === -1) {
    const lowerIdx = text.toLowerCase().indexOf(needle.toLowerCase(), from);
    if (lowerIdx === -1) {
      return null;
    }
    return {start: lowerIdx, end: lowerIdx + needle.length};
  }
  return {start: idx, end: idx + needle.length};
}

// LLM context often runs past the clause, so retry with fewer words
function findLoose(
  text: string,
  needle: string,
  side: "tail" | "head",
  from = 0,
): Span | null {
  const exact = findExact(text, needle, from);
  if (exact) {
    return exact;
  }
  const words = needle.split(/\s+/).filter(word => word.length > 0);
  for (
    let count = Math.min(MAX_CONTEXT_WORDS, words.length - 1);
    count >= MIN_CONTEXT_WORDS;
    count -= 1
  ) {
    const partial =
      side === "tail"
        ? words.slice(-count).join(" ")
        : words.slice(0, count).join(" ");
    const found = findExact(text, partial, from);
    if (found) {
      return found;
    }
  }
  return null;
}

interface UnitText {
  text: string;
  offsets: number[];
}

// Paragraph plain texts joined by newlines; offsets are each paragraph's start
function buildUnitText(paragraphs: PreviewParagraph[]): UnitText {
  const offsets: number[] = [];
  let text = "";
  paragraphs.forEach((paragraph, i) => {
    if (i > 0) {
      text += "\n";
    }
    offsets.push(text.length);
    if (paragraph.status !== "deleted") {
      text += plainText(paragraph.segments);
    }
  });
  return {text: normalizeQuotes(text), offsets};
}

function paragraphPlain(paragraph: PreviewParagraph): string {
  return clean(plainText(paragraph.segments));
}

function makeParagraph(
  atom: ClauseAtom,
  unitKey: string,
  hasLabel: boolean,
): PreviewParagraph {
  return {
    key: String(atom.id),
    atomId: String(atom.id),
    label: hasLabel ? formatAtomLabel(atom.reference, unitKey) : "",
    indent: getUnitDepth(atom.reference, unitKey),
    status: "kept",
    segments: [{kind: "kept", text: atom.text || ""}],
  };
}

function makeHeadingParagraph(
  heading: PreviewHeading,
  unitKey: string,
): PreviewParagraph {
  return {
    key: `heading-${String(heading.id ?? heading.reference)}`,
    label: formatAtomLabel(heading.reference, unitKey),
    indent: getUnitDepth(heading.reference, unitKey),
    status: "kept",
    isHeading: true,
    segments: [{kind: "kept", text: heading.text}],
  };
}

// Heading line first, then the atoms of that reference without a repeat label
function buildParagraphs(
  atoms: ClauseAtom[],
  headings: PreviewHeading[],
  unitKey: string,
): PreviewParagraph[] {
  const paragraphs: PreviewParagraph[] = [];
  const labelled = new Set<string>();
  // The unit's own heading, when its atom is empty and so not in the unit
  const unitReference = normaliseReference(unitKey);
  const unitHeading = headings.find(
    item => normaliseReference(item.reference) === unitReference,
  );
  const hasUnitAtom = atoms.some(
    atom => normaliseReference(atom.reference) === unitReference,
  );
  if (unitHeading && !hasUnitAtom && unitHeading.text.trim()) {
    paragraphs.push(makeHeadingParagraph(unitHeading, unitKey));
    labelled.add(unitReference);
  }
  atoms.forEach(atom => {
    const reference = normaliseReference(atom.reference);
    const heading = headings.find(
      item => normaliseReference(item.reference) === reference,
    );
    if (heading && !labelled.has(reference) && heading.text.trim()) {
      paragraphs.push(makeHeadingParagraph(heading, unitKey));
      labelled.add(reference);
    }
    paragraphs.push(makeParagraph(atom, unitKey, !labelled.has(reference)));
    labelled.add(reference);
  });
  return paragraphs;
}

export function markDeleted(paragraph: PreviewParagraph): void {
  paragraph.status = "deleted";
  paragraph.segments = paragraph.segments.map(segment =>
    segment.kind === "added" ? segment : {kind: "deleted", text: segment.text},
  );
}

// Whole-paragraph deletes: each line of deleted_text names one paragraph
function placeParagraphDelete(
  paragraphs: PreviewParagraph[],
  change: ProcessedClauseChange,
  unitKey: string,
): boolean {
  const lines = clean(change.deleted_text)
    .split("\n")
    .map(line => clean(stripClauseNumberPrefix(line)))
    .filter(line => line.length > 0);
  if (lines.length === 0) {
    return false;
  }
  const matched = lines
    .map(line =>
      paragraphs.find(paragraph => {
        if (paragraph.status !== "kept") {
          return false;
        }
        const text = clean(stripClauseNumberPrefix(paragraphPlain(paragraph)));
        // "(e) ...Supplier; and" names the item plus its conjunction line
        return (
          text.length > 0 &&
          (text === line || (text.length >= 10 && line.startsWith(text)))
        );
      }),
    )
    .filter((paragraph): paragraph is PreviewParagraph => Boolean(paragraph));
  if (matched.length === 0) {
    return false;
  }
  matched.forEach(markDeleted);
  deleteOrphanConjunctions(paragraphs);
  if (clean(change.added_text)) {
    const last = paragraphs.indexOf(matched[matched.length - 1]);
    paragraphs.splice(
      last + 1,
      0,
      ...buildInsertedParagraphs(change, unitKey, paragraphs[last].indent),
    );
  }
  return true;
}

const isConjunctionLine = (paragraph: PreviewParagraph): boolean =>
  paragraph.status === "kept" &&
  paragraphPlain(paragraph).split(/\s+/).length <= 2;

// A lone "and"/"or" between two deleted items goes with them
function deleteOrphanConjunctions(paragraphs: PreviewParagraph[]): void {
  paragraphs.forEach((paragraph, i) => {
    if (
      i > 0 &&
      i < paragraphs.length - 1 &&
      isConjunctionLine(paragraph) &&
      paragraphs[i - 1].status === "deleted" &&
      paragraphs[i + 1].status === "deleted"
    ) {
      markDeleted(paragraph);
    }
  });
}

function locateInline(
  unit: UnitText,
  change: ProcessedClauseChange,
): Span | null {
  const before = clean(change.replacement_context?.before);
  const after = clean(change.replacement_context?.after);
  const deleted = clean(change.deleted_text);

  let start = 0;
  let beforeFound = false;
  if (before) {
    const found = findLoose(unit.text, before, "tail");
    if (found) {
      start = found.end;
      beforeFound = true;
    }
  }

  if (deleted) {
    // Deleted text is rarer than a context word like "The"; anchor on it
    const byContext = locateByBothContexts(unit.text, deleted, before, after);
    if (byContext) {
      return byContext;
    }
    if (beforeFound) {
      const found = findExact(unit.text, deleted, start);
      // Deleted text must sit close to its before-context
      return found && found.start - start <= MAX_GAP_AFTER_BEFORE
        ? found
        : null;
    }
    return locateByAfterContext(unit.text, deleted, after);
  }

  if (beforeFound) {
    return {start, end: start};
  }
  if (after) {
    const found = findLoose(unit.text, after, "head");
    if (!found) {
      return null;
    }
    return {start: found.start, end: found.start};
  }
  return null;
}

const isWordChar = (char: string | undefined): boolean =>
  char !== undefined && /[\p{L}\p{N}]/u.test(char);

const isWholeWord = (text: string, span: Span): boolean =>
  !isWordChar(text[span.start - 1]) && !isWordChar(text[span.end]);

function findAll(text: string, needle: string): Span[] {
  const spans: Span[] = [];
  let found = findExact(text, needle, 0);
  while (found) {
    spans.push(found);
    found = findExact(text, needle, found.end);
  }
  return spans;
}

function isFollowedBy(text: string, pos: number, after: string): boolean {
  const rest = text.slice(pos).replace(/^\s+/, "");
  const words = after.split(/\s+/).filter(word => word.length > 0);
  const head = words
    .slice(0, Math.min(MAX_CONTEXT_WORDS, words.length))
    .join(" ");
  return head.length > 0 && rest.toLowerCase().startsWith(head.toLowerCase());
}

function isPrecededBy(text: string, pos: number, before: string): boolean {
  const head = text.slice(0, pos).replace(/\s+$/, "");
  const words = before.split(/\s+/).filter(word => word.length > 0);
  const tail = words
    .slice(-Math.min(MAX_CONTEXT_WORDS, words.length))
    .join(" ");
  return tail.length > 0 && head.toLowerCase().endsWith(tail.toLowerCase());
}

// The occurrence of the deleted text that its own contexts surround
function locateByBothContexts(
  text: string,
  deleted: string,
  before: string,
  after: string,
): Span | null {
  if (!before && !after) {
    return null;
  }
  const spans = findAll(text, deleted).filter(span => isWholeWord(text, span));
  const matches = spans.filter(
    span =>
      (!before || isPrecededBy(text, span.start, before)) &&
      (!after || isFollowedBy(text, span.end, after)),
  );
  return matches.length === 1 ? matches[0] : null;
}

// No before-context: pick the occurrence the after-context follows,
// else the only whole-word occurrence
function locateByAfterContext(
  text: string,
  deleted: string,
  after: string,
): Span | null {
  const spans = findAll(text, deleted);
  if (spans.length === 0) {
    return null;
  }
  if (after) {
    const byAfter = spans.find(span => isFollowedBy(text, span.end, after));
    if (byAfter) {
      return byAfter;
    }
  }
  const wholeWords = spans.filter(span => isWholeWord(text, span));
  if (wholeWords.length === 1) {
    return wholeWords[0];
  }
  return spans.length === 1 ? spans[0] : null;
}

function applySpan(
  paragraphs: PreviewParagraph[],
  unit: UnitText,
  span: Span,
  added: string,
): void {
  const isInsert = span.start === span.end;
  paragraphs.forEach((paragraph, i) => {
    if (paragraph.status === "deleted") {
      return;
    }
    const pStart = unit.offsets[i];
    const pEnd = pStart + plainText(paragraph.segments).length;
    const a = Math.max(span.start, pStart);
    const b = Math.min(span.end, pEnd);
    const holdsInsert = isInsert && span.start >= pStart && span.start <= pEnd;
    if (a >= b && !holdsInsert) {
      return;
    }
    const holdsEnd = span.end >= pStart && span.end <= pEnd;
    const coversAll = a === pStart && b === pEnd && pEnd > pStart;
    if (coversAll && !holdsEnd) {
      markDeleted(paragraph);
    } else if (coversAll && !added) {
      markDeleted(paragraph);
    } else {
      paragraph.segments = applyEdit(
        paragraph.segments,
        a - pStart,
        b - pStart,
        holdsEnd ? added : "",
      );
    }
  });
}

function placeInline(
  paragraphs: PreviewParagraph[],
  change: ProcessedClauseChange,
): boolean {
  const unit = buildUnitText(paragraphs);
  const span = locateInline(unit, change);
  if (!span) {
    return false;
  }
  applySpan(paragraphs, unit, span, change.added_text ?? "");
  return true;
}

function endsWithLoose(text: string, needle: string): boolean {
  const words = needle.split(/\s+/).filter(word => word.length > 0);
  const tail = words
    .slice(-Math.min(MAX_CONTEXT_WORDS, words.length))
    .join(" ");
  return tail.length > 0 && text.toLowerCase().endsWith(tail.toLowerCase());
}

function startsWithLoose(text: string, needle: string): boolean {
  const words = needle.split(/\s+/).filter(word => word.length > 0);
  const head = words
    .slice(0, Math.min(MAX_CONTEXT_WORDS, words.length))
    .join(" ");
  return head.length > 0 && text.toLowerCase().startsWith(head.toLowerCase());
}

interface InsertAnchor {
  index: number;
  insertBefore: boolean;
}

function findParagraphEndingWith(
  paragraphs: PreviewParagraph[],
  text: string,
): number {
  return paragraphs.findIndex(
    paragraph =>
      paragraph.status !== "deleted" &&
      endsWithLoose(paragraphPlain(paragraph), text),
  );
}

function findParagraphStartingWith(
  paragraphs: PreviewParagraph[],
  text: string,
): number {
  return paragraphs.findIndex(
    paragraph =>
      paragraph.status !== "deleted" &&
      startsWithLoose(paragraphPlain(paragraph), text),
  );
}

// Own-side context first; the other side places the insert just as well,
// e.g. after a heading that is not in the unit but before its first clause
function findInsertAnchor(
  paragraphs: PreviewParagraph[],
  change: ProcessedClauseChange,
): InsertAnchor | null {
  const before = clean(change.replacement_context?.before);
  const after = clean(change.replacement_context?.after);
  const byBefore = before ? findParagraphEndingWith(paragraphs, before) : -1;
  const byAfter = after ? findParagraphStartingWith(paragraphs, after) : -1;
  if (change.insert_after) {
    if (byBefore !== -1) {
      return {index: byBefore, insertBefore: false};
    }
    if (byAfter !== -1) {
      return {index: byAfter, insertBefore: true};
    }
  }
  if (change.insert_before) {
    if (byAfter !== -1) {
      return {index: byAfter, insertBefore: true};
    }
    if (byBefore !== -1) {
      return {index: byBefore, insertBefore: false};
    }
  }
  return null;
}

export function buildInsertedParagraphs(
  change: ProcessedClauseChange,
  unitKey: string,
  baseIndent: number,
): PreviewParagraph[] {
  const raw = change.rawAmendment;
  const subclauses = raw?.subclauses ?? [];
  if (subclauses.length > 0) {
    return subclauses.map((sub, i) => {
      const label = formatAtomLabel(sub.target_clause_reference, unitKey);
      return {
        key: `${change.id}-${i}`,
        label,
        indent: baseIndent + (Number(sub.level) || 0),
        status: "inserted",
        segments: [
          {
            kind: "added",
            text: label
              ? stripClauseNumberPrefix(sub.added_text)
              : sub.added_text,
          },
        ],
      };
    });
  }
  const label = formatAtomLabel(raw?.target_clause_reference, unitKey);
  const lines = (change.added_text ?? "")
    .split("\n")
    .map(line => line.trim())
    .filter(line => line.length > 0);
  return lines.map((line, i) => ({
    key: `${change.id}-${i}`,
    label: i === 0 ? label : "",
    indent: baseIndent,
    status: "inserted",
    segments: [
      {
        kind: "added",
        text: i === 0 && label ? stripClauseNumberPrefix(line) : line,
      },
    ],
  }));
}

function placeParagraphInsert(
  paragraphs: PreviewParagraph[],
  change: ProcessedClauseChange,
  unitKey: string,
  atoms: ClauseAtom[],
): boolean {
  let anchor = findInsertAnchor(paragraphs, change);
  if (!anchor) {
    const reference = normaliseReference(change.clause_reference);
    const atomIds = atoms
      .filter(atom => normaliseReference(atom.reference) === reference)
      .map(atom => String(atom.id));
    const indexes = paragraphs
      .map((paragraph, i) =>
        paragraph.atomId && atomIds.includes(paragraph.atomId) ? i : -1,
      )
      .filter(i => i !== -1);
    if (indexes.length === 0) {
      return false;
    }
    anchor = change.insert_before
      ? {index: indexes[0], insertBefore: true}
      : {index: indexes[indexes.length - 1], insertBefore: false};
  }
  const baseIndent = paragraphs[anchor.index].indent;
  const inserted = buildInsertedParagraphs(change, unitKey, baseIndent);
  const at = anchor.insertBefore ? anchor.index : anchor.index + 1;
  paragraphs.splice(at, 0, ...inserted);
  return true;
}

function tryPlace(
  paragraphs: PreviewParagraph[],
  change: ProcessedClauseChange,
  unitKey: string,
  atoms: ClauseAtom[],
): boolean {
  if (change.insert_before || change.insert_after) {
    return placeParagraphInsert(paragraphs, change, unitKey, atoms);
  }
  const spansParagraphs =
    change.delete || clean(change.deleted_text).includes("\n");
  if (spansParagraphs && placeParagraphDelete(paragraphs, change, unitKey)) {
    return true;
  }
  return placeInline(paragraphs, change);
}

function isParagraphTouched(paragraph: PreviewParagraph): boolean {
  return paragraph.status !== "kept" || isTouched(paragraph.segments);
}

export function collapseUnchanged(
  paragraphs: PreviewParagraph[],
): PreviewParagraph[] {
  const total = paragraphs.reduce(
    (sum, paragraph) => sum + plainText(paragraph.segments).length,
    0,
  );
  if (total <= COLLAPSE_ABOVE_CHARS) {
    return paragraphs;
  }
  const keep = paragraphs.map(
    (paragraph, i) =>
      isParagraphTouched(paragraph) ||
      (i > 0 && isParagraphTouched(paragraphs[i - 1])) ||
      (i < paragraphs.length - 1 && isParagraphTouched(paragraphs[i + 1])),
  );
  const out: PreviewParagraph[] = [];
  paragraphs.forEach((paragraph, i) => {
    if (keep[i]) {
      out.push(paragraph);
    } else {
      const last = out[out.length - 1];
      if (last && last.status === "collapsed") {
        last.collapsedCount = (last.collapsedCount ?? 0) + 1;
      } else {
        out.push({
          key: `collapsed-${i}`,
          label: "",
          indent: paragraph.indent,
          status: "collapsed",
          segments: [],
          collapsedCount: 1,
        });
      }
    }
  });
  return out;
}

export function getUnitAtoms(
  atoms: ClauseAtom[],
  unitKey: string,
  sectionId: unknown,
): ClauseAtom[] {
  return atoms
    .filter(
      atom =>
        (sectionId === undefined || atom.section_id === sectionId) &&
        isInUnit(atom.reference, unitKey) &&
        (atom.text || "").trim().length > 0,
    )
    .sort((a, b) => (a.clause_order ?? 0) - (b.clause_order ?? 0));
}

export default function buildUnitPreview(
  unitKey: string,
  atoms: ClauseAtom[],
  changes: ProcessedClauseChange[],
  headings: PreviewHeading[] = [],
): UnitPreview {
  const paragraphs = buildParagraphs(atoms, headings, unitKey);

  // Two passes: chained edits anchor on text another edit inserts
  let pending = [...changes];
  for (let pass = 0; pass < 2 && pending.length > 0; pass += 1) {
    const remaining: ProcessedClauseChange[] = [];
    pending.forEach(change => {
      if (!tryPlace(paragraphs, change, unitKey, atoms)) {
        remaining.push(change);
      }
    });
    pending = remaining;
  }

  return {paragraphs: collapseUnchanged(paragraphs), unplaced: pending};
}

export const previewToText = (preview: UnitPreview): string =>
  preview.paragraphs
    .map(paragraph => {
      if (paragraph.status === "collapsed") {
        return `[${paragraph.collapsedCount} unchanged]`;
      }
      const body = paragraph.segments
        .map(segment => {
          if (segment.kind === "deleted") {
            return `[-${segment.text}-]`;
          }
          if (segment.kind === "added") {
            return `[+${segment.text}+]`;
          }
          return segment.text;
        })
        .join("");
      const label = paragraph.label ? `${paragraph.label} ` : "";
      return `${"  ".repeat(paragraph.indent)}${label}${body}`;
    })
    .join("\n");

export const isCleanSegment = (segment: PreviewSegment): boolean =>
  segment.kind === "kept";
